import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { MIN_PLAYERS, type GameStateSnapshot, type RoomConfig } from '@juegazo/shared';
import { loadLocalConfig, saveLocalConfig } from '../lib/local-config';
import { gameStore, type GameStoreState } from '../lib/game-store';
import { loadSession, saveSession } from '../lib/game-store';
import { VoiceManager } from '../lib/voice-manager';
import './home-view';
import './lobby-view';
import './local-view';
import './play-view';
import './result-view';
import './waiting-view';

type Route =
  | { name: 'home' }
  | { name: 'lobby'; roomId: string; playerId: string; isAdmin: boolean }
  | { name: 'play'; roomId: string; playerId: string; isAdmin: boolean }
  | { name: 'waiting'; roomId: string }
  | { name: 'result' }
  | { name: 'local' };

@customElement('juegazo-app')
export class JuegazoApp extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state() private route: Route = { name: 'home' };
  @state() private store: GameStoreState = gameStore.getState();
  @state() private session = loadSession();
  @state() private toast: string | null = null;
  @state() private toastVariant: 'error' | 'info' = 'error';
  /** Error de la pantalla de inicio (entrar/crear sala): se muestra en el formulario. */
  @state() private joinError: string | null = null;
  @state() private voiceManager: VoiceManager | null = null;
  @state() private showReconnectBanner = false;
  private toastTimer: number | null = null;
  private staleTimer: number | null = null;

  private unsub: (() => void) | null = null;
  private lastVoiceRoomId: string | null = null;
  /** Último error de socket ya informado, para no repetir el mismo toast en cada reintento. */
  private lastSurfacedError: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    gameStore.connect();
    this.unsub = gameStore.subscribe((s) => {
      this.store = s;
      // Un fallo de transporte NO expulsa al usuario en todos los casos:
      //  - el juego local es offline: un socket caído no corta la partida;
      //  - en el lobby, el usuario se queda (el lobby muestra su propio
      //    escape, VOLVER AL INICIO) y solo se vuelve al inicio cuando el
      //    error invalida la sesión (reconnect_failed / reconnect_timeout).
      let mustGoHome = false;
      if (s.lastError) {
        const code = s.lastError.code;
        if (
          code === 'reconnect_failed' ||
          code === 'reconnect_timeout' ||
          code === 'socket_connect_error' ||
          code === 'socket_reconnect_failed'
        ) {
          if (this.route.name !== 'home') {
            const errorKey = `${code}:${s.lastError.message}`;
            if (errorKey !== this.lastSurfacedError) {
              this.lastSurfacedError = errorKey;
              this.showToast(s.lastError.message);
            }
            const sessionLost = code === 'reconnect_failed' || code === 'reconnect_timeout';
            const staysInLobby = this.route.name === 'lobby' && !sessionLost;
            mustGoHome = this.route.name !== 'local' && !staysInLobby;
          }
        }
      } else {
        this.lastSurfacedError = null;
      }
      if (mustGoHome) {
        this.goHome();
        return;
      }
      if (s.lastPlayerLeft) {
        const info = s.lastPlayerLeft;
        const reconnected = s.state?.players.some(
          (p) => p.id === info.playerId && p.isConnected,
        );
        if (reconnected) {
          gameStore.clearLastPlayerLeft();
        } else {
          const reason = info.reason === 'leave' ? 'salió de la partida' : 'se desconectó';
          this.showToast(`${info.displayName} ${reason}`, 'info', 5000);
          gameStore.clearLastPlayerLeft();
        }
      }
      if (s.lastPlayerReconnected) {
        const info = s.lastPlayerReconnected;
        this.showToast(`✅ ${info.displayName} volvió a la partida`, 'info', 5000);
        gameStore.clearLastPlayerReconnected();
      }
      this.handleStaleConnection();
      this.syncVoiceManager();
      this.handleRouteFromState();
    });
    this.handleRouteFromState();
    window.addEventListener('hashchange', this.onHash);
    this.parseHash();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsub?.();
    window.removeEventListener('hashchange', this.onHash);
    this.disposeVoiceManager();
    if (this.staleTimer !== null) {
      clearTimeout(this.staleTimer);
      this.staleTimer = null;
    }
  }

  /**
   * Si el socket esta desconectado y tenemos session activa, despues de 3s
   * sin recuperacion automatica mostramos un banner con un boton "Reconectar"
   * para que el usuario dispare un ciclo programatico disconnect+connect del
   * socket (equivalente a F5 sin recargar la pagina). Es un escape hatch:
   * el usuario no necesita saber que existe la session, ni como funciona el
   * ciclo socket.io; solo ve "Reconectar" cuando algo esta mal.
   */
  private handleStaleConnection(): void {
    if (this.store.connected) {
      this.showReconnectBanner = false;
      if (this.staleTimer !== null) {
        clearTimeout(this.staleTimer);
        this.staleTimer = null;
      }
      return;
    }
    if (!this.session.roomId) return;
    if (this.staleTimer !== null) return;
    this.staleTimer = window.setTimeout(() => {
      this.staleTimer = null;
      if (!this.store.connected && this.session.roomId) {
        this.showReconnectBanner = true;
      }
    }, 3000);
  }

  private handleReconnectClick = (): void => {
    gameStore.forceReconnect();
    this.showReconnectBanner = false;
    if (this.staleTimer !== null) {
      clearTimeout(this.staleTimer);
      this.staleTimer = null;
    }
  };

  private syncVoiceManager(): void {
    const state = this.store.state;
    const socket = gameStore.getSocket();
    if (!state || !socket) {
      this.disposeVoiceManager();
      return;
    }
    if (this.lastVoiceRoomId === state.roomId && this.voiceManager) return;
    this.disposeVoiceManager();
    this.voiceManager = new VoiceManager({ socket });
    // El manager vive en app-root; desde acá indicamos que cualquier
    // <jgz-voice-controls> que monte a continuación debe activar el micrófono
    // automáticamente. La primera vista que renderice el botón lo consume y
    // start(); cambios de vista posteriores ya no disparan auto-start.
    this.voiceManager.enableOnMount();
    this.lastVoiceRoomId = state.roomId;
  }

  private disposeVoiceManager(): void {
    if (!this.voiceManager) return;
    try {
      this.voiceManager.stop();
    } catch {
      // ignore
    }
    this.voiceManager.destroy();
    this.voiceManager = null;
    this.lastVoiceRoomId = null;
  }

  private onHash = () => this.parseHash();

  private parseHash() {
    const hash = window.location.hash.replace(/^#\/?/, '');
    if (!hash) {
      this.route = { name: 'home' };
      return;
    }
    const [name, ...rest] = hash.split('/');
    const activeRoomId = this.session.roomId ?? this.store.state?.roomId ?? null;
    if (name === 'lobby' && rest[0]) {
      const roomId = rest[0];
      if (activeRoomId !== roomId) {
        this.showToast('Debes unirte a una sala desde el inicio');
        this.route = { name: 'home' };
        window.location.hash = '#/';
        return;
      }
      this.route = { name: 'lobby', roomId, playerId: this.session.playerId ?? '', isAdmin: this.session.playerId === this.store.state?.players.find((p) => p.isAdmin)?.id };
    } else if (name === 'play' && rest[0]) {
      if (activeRoomId !== rest[0]) {
        this.showToast('Debes unirte a una sala desde el inicio');
        this.route = { name: 'home' };
        window.location.hash = '#/';
        return;
      }
      this.route = { name: 'play', roomId: rest[0], playerId: this.session.playerId ?? '', isAdmin: false };
    } else if (name === 'waiting' && rest[0]) {
      if (activeRoomId !== rest[0]) {
        this.showToast('Debes unirte a una sala desde el inicio');
        this.route = { name: 'home' };
        window.location.hash = '#/';
        return;
      }
      this.route = { name: 'waiting', roomId: rest[0] };
    } else if (name === 'local') {
      this.route = { name: 'local' };
    } else if (name === 'result') {
      this.route = { name: 'result' };
    } else {
      this.route = { name: 'home' };
    }
  }

  private handleRouteFromState() {
    const state = this.store.state;
    if (!state) return;
    if (state.state === 'finished' && this.route.name !== 'result') {
      this.route = { name: 'result' };
      window.location.hash = '#/result';
      return;
    }
    if (this.route.name === 'result' && this.isPlayingState(state.state) && state.roomId) {
      this.route = { name: 'play', roomId: state.roomId, playerId: this.session.playerId ?? '', isAdmin: false };
      window.location.hash = `#/play/${state.roomId}`;
      return;
    }
    if (state.state === 'lobby' && state.roomId && this.route.name !== 'lobby') {
      this.route = { name: 'lobby', roomId: state.roomId, playerId: this.session.playerId ?? '', isAdmin: false };
      window.location.hash = `#/lobby/${state.roomId}`;
      return;
    }
    if (this.route.name === 'lobby' && this.isPlayingState(state.state) && state.roomId) {
      this.route = { name: 'play', roomId: state.roomId, playerId: this.session.playerId ?? '', isAdmin: false };
      window.location.hash = `#/play/${state.roomId}`;
      return;
    }
    if (this.route.name === 'play' && state.state === 'lobby' && state.roomId) {
      window.location.hash = `#/lobby/${state.roomId}`;
      return;
    }
    if (this.route.name === 'home' && state.roomId) {
      if (state.state === 'lobby') {
        this.route = { name: 'lobby', roomId: state.roomId, playerId: this.session.playerId ?? '', isAdmin: false };
        window.location.hash = `#/lobby/${state.roomId}`;
      } else if (this.isPlayingState(state.state)) {
        this.route = { name: 'play', roomId: state.roomId, playerId: this.session.playerId ?? '', isAdmin: false };
        window.location.hash = `#/play/${state.roomId}`;
      }
    }
  }

  /**
   * Estados en los que la partida está en curso: la sala muestra la pantalla
   * de juego. Es la ÚNICA lista: la usan el enrutado por estado y la entrada
   * a una sala que ya empezó.
   *
   * El motor NUNCA emite 'countdown' (ver `packages/game-core/src/index.ts`):
   * ese estado no se considera.
   */
  private isPlayingState(s: GameStateSnapshot['state']): boolean {
    return (
      s === 'playing' ||
      s === 'paused' ||
      s === 'exploded' ||
      s === 'between_bombs' ||
      s === 'tiebreak_playing' ||
      s === 'tiebreak_ready'
    );
  }

  private navigate = (path: string) => {
    window.location.hash = path;
  };

  private showToast = (message: string, variant: 'error' | 'info' = 'error', durationMs = 3000) => {
    if (this.toastTimer !== null) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
    this.toast = message;
    this.toastVariant = variant;
    this.toastTimer = window.setTimeout(() => {
      this.toast = null;
      this.toastTimer = null;
    }, durationMs);
  };

  private dismissToast = () => {
    if (this.toastTimer !== null) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
    this.toast = null;
  };

  private goHome = () => {
    // La ruta se cambia ANTES de `leaveRoom()`: `leaveRoom` notifica la store
    // de forma síncrona y el suscriptor de `app-root` volvería a entrar en
    // `goHome()` (mientras la ruta sigue en 'lobby') → recursión infinita
    // ("Maximum call stack size exceeded") cuando el socket está caído.
    this.route = { name: 'home' };
    window.location.hash = '#/';
    // `leaveRoom` es el único dueño del aviso al servidor: sin él, el
    // servidor conservaba la silla del jugador como fantasma en la sala.
    gameStore.leaveRoom();
    window.localStorage.removeItem('juegazo.localPlayers');
    this.session = loadSession();
  };

  private onJoin = async (e: CustomEvent<{ mode: 'create' | 'join'; displayName: string; roomId?: string }>) => {
    const { mode, displayName, roomId } = e.detail;
    this.joinError = null;
    if (mode === 'create') {
      const resp = await gameStore.createRoom(displayName);
      if (resp.ok) {
        saveSession({
          roomId: resp.roomId,
          playerId: resp.playerId,
          displayName,
          sessionToken: resp.sessionToken,
        });
        this.session = loadSession();
        this.navigate(`#/lobby/${resp.roomId}`);
      } else {
        this.joinError = resp.error;
      }
    } else if (mode === 'join' && roomId) {
      const resp = await gameStore.joinRoom(roomId, displayName);
      if (resp.ok) {
        saveSession({
          roomId: roomId.toUpperCase(),
          playerId: resp.playerId,
          displayName,
          sessionToken: resp.sessionToken,
        });
        this.session = loadSession();
        const snap = resp.state;
        if (snap.state === 'lobby') {
          this.navigate(`#/lobby/${roomId.toUpperCase()}`);
        } else if (this.isPlayingState(snap.state)) {
          this.navigate(`#/play/${roomId.toUpperCase()}`);
        } else if (snap.state === 'finished') {
          this.navigate('#/result');
        } else {
          this.navigate(`#/waiting/${roomId.toUpperCase()}`);
        }
      } else {
        // El jugador se queda en el formulario con el motivo: puede corregir el
        // código o el nombre e intentar de nuevo.
        this.joinError = `No se pudo unir a la sala: ${resp.error}`;
      }
    }
  };

  private onLocal = (e: CustomEvent<{ displayName: string; playerNames: string[]; config: RoomConfig }>) => {
    const names = e.detail.playerNames.filter((n) => n.trim().length > 0);
    if (names.length < MIN_PLAYERS) return;
    saveSession({ roomId: null, playerId: null, displayName: names[0], sessionToken: null });
    this.session = { ...this.session, roomId: null, playerId: null, displayName: names[0] };
    window.localStorage.setItem('juegazo.localPlayers', JSON.stringify(names));
    saveLocalConfig(e.detail.config);
    window.localStorage.removeItem('juegazo.localFinalResult');
    window.localStorage.removeItem('juegazo.localGameState');
    this.route = { name: 'local' };
    window.location.hash = '#/local';
  };

  private onLeaveHome = () => {
    // Mismo orden que `goHome`: la ruta primero (evita la recursión con el
    // socket caído), la salida de la sala después.
    this.route = { name: 'home' };
    window.location.hash = '#/';
    gameStore.leaveRoom();
    this.session = loadSession();
  };

  private loadLocalPlayers(): string[] {
    try {
      const raw = window.localStorage.getItem('juegazo.localPlayers');
      if (!raw) return [];
      const names = JSON.parse(raw) as unknown;
      if (Array.isArray(names)) return names.filter((n): n is string => typeof n === 'string' && n.trim().length > 0);
    } catch {
      // ignore
    }
    return [];
  }

  render() {
    const state = this.store.state;
    return html`
      <div class="app-shell ${state?.tensionState ? 'tension-' + state.tensionState : 'tension-normal'}">
        ${this.renderRoute()}
      </div>
      ${this.toast ? this.renderToast() : nothing}
      ${this.showReconnectBanner ? this.renderReconnectBanner() : nothing}
    `;
  }

  private renderReconnectBanner() {
    return html`
      <div class="reconnect-banner" role="alert">
        <span class="reconnect-icon" aria-hidden="true">⚠</span>
        <span class="reconnect-message">Sin conexión con el servidor</span>
        <button class="reconnect-button" @click=${this.handleReconnectClick}>Reconectar</button>
      </div>
    `;
  }

  private renderToast() {
    const variant = this.toastVariant;
    return html`
      <div class="toast ${variant}" role="alert" @click=${this.dismissToast}>
        <span class="toast-icon" aria-hidden="true">${variant === 'error' ? '⚠' : 'ℹ'}</span>
        <span class="toast-message">${this.toast}</span>
      </div>
    `;
  }

  private renderRoute() {
    switch (this.route.name) {
      case 'home':
        return html`<jgz-home
          .displayName=${this.session.displayName ?? ''}
          .error=${this.joinError}
          @jgz-join=${this.onJoin}
          @jgz-error-clear=${() => (this.joinError = null)}
          @jgz-local=${this.onLocal}
        ></jgz-home>`;
      case 'lobby':
        return html`<jgz-lobby
          .roomId=${this.route.roomId}
          .state=${this.store.state}
          .playerId=${this.session.playerId}
          .voiceManager=${this.voiceManager}
          .lastError=${this.store.lastError}
          @jgz-leave=${this.onLeaveHome}
        ></jgz-lobby>`;
      case 'play':
        return html`<jgz-play
          .roomId=${this.route.roomId}
          .state=${this.store.state}
          .lastExplosion=${this.store.lastExplosion}
          .voiceManager=${this.voiceManager}
          .lastError=${this.store.lastError}
          @jgz-leave=${this.onLeaveHome}
        ></jgz-play>`;
      case 'local':
        return html`<jgz-local
          .playerNames=${this.loadLocalPlayers()}
          .config=${loadLocalConfig()}
          @jgz-leave=${this.onLeaveHome}
        ></jgz-local>`;
      case 'waiting':
        return html`<jgz-waiting
          .roomId=${this.route.roomId}
          .state=${this.store.state}
          @jgz-leave=${this.onLeaveHome}
        ></jgz-waiting>`;
      case 'result':
        return html`<jgz-result
          .state=${this.store.state}
          .result=${this.store.lastFinished}
          @jgz-leave=${this.onLeaveHome}
        ></jgz-result>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'juegazo-app': JuegazoApp;
  }
}
